<template>
  <div class="privacy-policy-page">
    <div class="content-container">
      <header class="policy-header">
        <h1>PRIVACY POLICY</h1>
        <p class="last-updated">Last updated: [31/12/2025]</p>
      </header>

      <section class="intro-section">
        <p>This website (hereinafter referred to as “this website” or “Alyhome”) is operated by Hangzhou Bailian Hechuang Technology Co., Ltd., (hereinafter referred to as “we” or “the company”). This website is mainly used for cross-border B2B business cooperation, providing product information display, cooperative communication and order management related services to corporate/registered business customers and their authorized personnel.</p>
        <p>This website serves as a platform primarily focused on cross-border B2B cooperation. It offers services to corporate and registered business customers, as well as their authorized staff, including the display of product information, facilitation of cooperative communication, and order management. This website does not provide services to individual consumers.</p>
        <p>We value our customers and respect your privacy. This Privacy Policy describes how we collect, use, and disclose information through and on behalf of websites and mobile applications operated by or on behalf of Alyhome, including Alyhome.com and other websites and mobile applications owned and controlled by us (collectively, the “Alyhome Online Platforms”).</p>
        <p>This Privacy Policy also applies to information collected through offline channels, such as through our showrooms, trade show booths, or sales representatives (collectively, “Alyhome Offline Entities”).</p>
        <p>By using the Alyhome online platform, visiting our offline entities, or providing us with information, you consent to the collection, use, and disclosure of your information as described in this Privacy Policy.</p>
      </section>

      <!-- 1. The personal information we collect -->
      <section class="policy-section">
        <h2 class="section-title">1. The personal information we collect</h2>
        <div class="sub-content">
          <h3>Information Collected from Regular Registered Users:</h3>
          <p>Regular users are restricted to viewing publicly displayed product information and cannot place orders. For these users, we may collect the following personal details:</p>
          <ul>
            <li>Name</li>
            <li>Email Address</li>
            <li>Company Name</li>
            <li>Login username and password (stored using encryption)</li>
          </ul>

          <h3>Information Collected from Approved Enterprise Users:</h3>
          <p>Once you are approved as an enterprise user, we may record details related to our collaboration, including:</p>
          <ul>
            <li>Company name and contact information</li>
            <li>Historical order records (e.g., product name, quantity, price, and time of order)</li>
            <li>Delivery information (address, recipient's name, recipient's phone number, and logistics details)</li>
          </ul>

          <h3>Information about Other Individuals:</h3>
          <p>By providing us with the personal information of other individuals, you confirm that you are authorized to do so and permit us to use that information in accordance with this Privacy Policy.</p>

          <h3>Payment Information:</h3>
          <p>This website does not offer online payment services, and as such, we do not collect any bank card or credit card information through the site.</p>
        </div>
      </section>

      <!-- 2. How We Use Your Personal Information -->
      <section class="policy-section">
        <h2 class="section-title">2. How We Use Your Personal Information</h2>
        <p>We utilize your personal information for the following business operations:</p>
        
        <div class="table-container">
          <table class="policy-table">
            <thead>
              <tr>
                <th>Purpose</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Fulfill Requests</td>
                <td>To record and process cooperation intentions or order requests.</td>
              </tr>
              <tr>
                <td>Communication</td>
                <td>To respond to your inquiries, provide customer support, and send essential administrative information (such as updates to our terms or policies).</td>
              </tr>
              <tr>
                <td>Business Recommendations</td>
                <td>To inform you about our products, services, product catalogs, and send business recommendations via email, mail, or telephone.</td>
              </tr>
              <tr>
                <td>Personalization</td>
                <td>To better understand your preferences and customize our services and product catalog to meet your specific business needs.</td>
              </tr>
              <tr>
                <td>Analysis</td>
                <td>To analyze trends, usage patterns, and measure the effectiveness of our business recommendation activities.</td>
              </tr>
              <tr>
                <td>Safety and Compliance</td>
                <td>To ensure the security and integrity of our business operations, prevent fraud, and comply with all applicable legal procedures and government requirements.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>How do we disclose personal information?</h3>
        <p>We may disclose your personal information to the following entities:</p>
        <ul>
          <li><strong>Third-party providers</strong> that offer services such as website hosting, data storage, order recording, IT infrastructure, email sending, and auditing.</li>
          <li><strong>Affiliated institutions:</strong> Our subsidiaries and affiliates may use this information for their business recommendations and purposes.</li>
          <li><strong>Legal and commercial transfer:</strong> We will disclose to third parties any reorganization, merger, sale, joint venture, assignment, transfer or other disposal of all or part of our business or assets (including disposal in connection with any bankruptcy or similar proceedings).</li>
        </ul>

        <h3>How Your Personal Information May Be Shared</h3>
        <p>We may share your personal information with the following categories of recipients:</p>
        <ul>
          <li><strong>Service Providers:</strong> We engage third-party companies to provide essential services, including website hosting, data storage, processing orders, IT support, email communications, and auditing.</li>
          <li><strong>Affiliates:</strong> Your information may be shared with our subsidiaries and affiliated entities for their business purposes, such as making recommendations.</li>
          <li><strong>Business Transfers and Legal Proceedings:</strong> In the event of a merger, acquisition, sale, joint venture, assignment, transfer, or other disposition of all or part of our business or assets (including bankruptcy or similar proceedings), we will disclose your information to the relevant third parties involved.</li>
        </ul>
      </section>

      <!-- 3. Business profiling and recommendations -->
      <section class="policy-section">
        <h2 class="section-title">3. Business profiling and recommendations</h2>
        <p>We may create internal profiles for enterprise users based on company information, cooperation history, and interactions with the website, for the following purposes:</p>
        <ul>
          <li>Assess cooperation potential</li>
          <li>Recommend relevant products or solutions</li>
          <li>Improve business communication efficiency</li>
        </ul>
        <p>Such profiles are limited to business-related information, do not involve sensitive personal data, and will not be used for consumer advertising purposes.</p>
        <p>These commercial recommendations are based on our legitimate interest in maintaining business relationships with our existing corporate clients. You always have the right to opt out of these marketing recommendations using the methods described in Section 7.</p>
      </section>

      <!-- 4. Other Information and Technology -->
      <section class="policy-section">
        <h2 class="section-title">4. Other Information and Technology</h2>
        <p>We collect “other information” that typically does not identify you personally, such as browser information, app usage data, and aggregated data.</p>
        <p>We use cookies or similar technologies to ensure basic website functionality, remember login status, and track visit history. You can manage or delete cookies through your browser settings, but this may affect the use of some functions. For non-essential cookies or similar technologies, we will obtain your explicit consent via a banner upon your first visit. You can adjust your preferences at any time through your browser settings or our consent management tool.</p>
        
        <h3>Other Information We Collect</h3>
        <p>We collect certain non-personally identifiable information, which we refer to as "other information." This typically includes data like your browser details, how you use our application, and aggregated statistical data.</p>
        
        <h3>Our Use of Cookies and Similar Technologies</h3>
        <p>We use cookies or comparable technologies for essential website functions, such as maintaining your login status and tracking your visit history. You have the ability to manage or delete these cookies via your browser settings, though doing so might affect the functionality of some features. For any cookies or similar technologies that are not essential, we will request your clear permission through a banner when you first visit our site.</p>
      </section>

      <!-- 5 & 6. Security and data storage (Combined based on text duplication in image) -->
      <section class="policy-section">
        <h2 class="section-title">5. Security and Data Storage</h2>
        <p>We are committed to taking reasonable organizational, technical and managerial measures to protect personal information within our organization, including the use of Secure Sockets Layer (SSL) technology.</p>
        
        <h3>Data storage and hosting</h3>
        <p>This website's data is stored on United States servers to support the stable operation of our cross-border business systems. We have signed a data processing agreement to ensure that such data is not automatically mirrored to servers outside the United States.</p>
        
        <h3>Data retention period</h3>
        <p>We will retain your personal information only for the time necessary to achieve the purposes described in this policy. The retention period will be determined based on the type of information, business needs, and legal obligations. When your data is no longer needed, we will securely delete or anonymize it.</p>
        
        <h3>International visits (technical support)</h3>
        <p>Although your data is stored in the United States, it may be accessed by authorized personnel outside of China and within necessary limits, but only for system maintenance, security monitoring, and technical support. We implement strict access control measures (such as VPN tunneling and access logging) to ensure the security of your data.</p>
      </section>

      <!-- 7. Your Privacy Rights -->
      <section class="policy-section">
        <h2 class="section-title">7. Your Privacy Rights</h2>
        <p>Depending on your location, you may have specific rights regarding your personal information, which include the right to:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of the personal information we have collected about you over the past 12 months.</li>
          <li><strong>Correction:</strong> Ask us to correct any inaccurate information we hold about you.</li>
          <li><strong>Deletion:</strong> Request the deletion of your personal information, subject to certain legal exceptions.</li>
          <li><strong>Opt-Out of "Sale" or "Sharing":</strong> Be assured that we do not sell your personal information for profit.</li>
        </ul>
        <p>You can exercise these rights by:</p>
        <ul>
          <li>Submitting a Web <strong style="text-decoration: underline; cursor: pointer;" @click="handleClick()">Form</strong> (+86-574-62130152).</li>
          <li>Calling Us: Contact us by +86-574-62130152.</li>
          <li>Opting Out of Promotional Communications: Click the "unsubscribe" link in any such email.</li>
        </ul>
      </section>

      <!-- 8 & 9. California Residents -->
      <section class="policy-section">
        <h2 class="section-title">8. Notice to California Residents</h2>
        <p>If you are a California resident, the CCPA and CPRA provide you with specific rights. In the past 12 months, we have collected: Identifiers, Business Information, and Internet Activity Information.</p>
        
        <h2 class="section-title">9. Your Privacy Rights (CA)</h2>
        <ul>
          <li><strong>The Right to Know / Access:</strong> To be informed and receive a copy of your data.</li>
          <li><strong>The Right to Delete:</strong> Request deletion of personal info.</li>
          <li><strong>The Right to Opt-Out:</strong> Opt out of "sale" or "sharing". We do not currently sell information for money.</li>
        </ul>
        <p><strong>Exercising Your Rights</strong> </p>
        <div>
          You can submit a verifiable consumer request to exercise your rights by visiting  <strong style="text-decoration: underline; cursor: pointer;" @click="handleClick()">Form</strong> or by calling +86-574-62130152. We commit to responding to your request within 45 days
        </div>
         <p style="margin-top: 20px;"><strong>Non-Discrimination</strong></p>
        <div>
          You will not face any discriminatory treatment for exercising your CCPA and CPRA rights.
        </div>
      </section>

      <!-- 10 & 11. Minors and Law -->
      <section class="policy-section">
        <h2 class="section-title">10. Minors' Privacy</h2>
        <p>The Alyhome online platform is not intended for use by individuals under 18 years of age. We explicitly request that minors do not submit any personal information.</p>

        <h2 class="section-title">11. Governing Law and Jurisdiction</h2>
        <p>In processing personal information, we adhere to applicable laws. Should any part of this policy conflict with mandatory privacy legislation in your jurisdiction, the provisions of those mandatory laws will take precedence.</p>
      </section>
    </div>

  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
  methods: {
    handleClick() {
      this.$router.push({ path: '/privacyApply' });
      window.scrollTo({ top: 0,behavior: "instant"});
    }
  }
}
</script>

<style scoped>
.privacy-policy-page {
  background-color: #FAF9F7;
  color: #333;
  font-family:"NotoSerif", sans-serif;
  line-height: 1.6;
  padding: 40px 20px 60px;
  display: flex;
  justify-content: center;
}

.content-container {
  max-width: 900px;
  width: 100%;
  background: transparent;
}

.policy-header {
  text-align: center;
  margin-bottom: 40px;
}

.policy-header h1 {
  font-size: 28px;
  font-weight: bold;
  letter-spacing: 1px;
  margin-bottom: 10px;
}

.last-updated {
  font-size: 16px;
  color: #666;
  /* text-align: left; */
  font-weight: bold;
}

.intro-section p {
  margin-bottom: 15px;
  font-size: 16px;
}

.policy-section {
  margin-top: 40px;
}

.section-title {
  font-size: 20px;
  font-weight: bold;
  border-bottom: none;
  margin-bottom: 20px;
}

h3 {
  font-size: 16px;
  font-weight: bold;
  margin: 20px 0 10px 0;
}

p {
  margin-bottom: 15px;
  font-size: 16px;
}

ul {
  margin-bottom: 20px;
  padding-left: 45px; /* 稍微增加一点缩进，确保圆点显示完整 */
}

li {
  margin-bottom: 8px;
  font-size: 16px;
  list-style-type: disc !important; 
  display: list-item; 
}

/* Table Styles */
.table-container {
  margin: 20px 0;
  overflow-x: auto;
}

.policy-table {
  width: 100%;
  border-collapse: collapse;
  background-color: #fff;
  border: 1px solid #eee;
}

.policy-table th, .policy-table td {
  border: 1px solid #eee;
  padding: 12px 15px;
  text-align: left;
  font-size: 16px;
}

.policy-table th {
  background-color: #f2f2f2;
  font-weight: bold;
}

@media (max-width: 600px) {
  .policy-header h1 {
    font-size: 22px;
  }
  .section-title {
    font-size: 18px;
  }
}
@media (max-width: 768px) {
  .section-title,h1{
    font-size: 4rem !important;
  }
  td,th,h3,p,li,div{
     font-size: 3rem !important;
  }
  .content-container{
    max-width: 90%;
  }
}
</style>